// src/components/common/Fachbegriff.jsx
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';

/**
 * Fachbegriff — englisches Etikett mit deutschem Rückverweis, tastaturerreichbar.
 *
 * @description
 *   Zeigt einen englischen Fachbegriff und trägt den deutschen Begriff als
 *   Tooltip. Vorgabe: der Auslöser ist FOKUSSIERBAR (`tabIndex={0}`; Ausnahme `fokussierbar={false}`, s. @props) — ein `title` auf
 *   einem nicht fokussierbaren Element (`<dt>`, `<th>`, `<h2>`, `<label>`) ist
 *   per Tastatur unerreichbar und macht keine Zusage an assistive Technik
 *   (WCAG H65 / 1.4.13; Regel-16-Warnung W1, Welle 3 PR 3). `describeChild`
 *   macht den SICHTBAREN Text zum Namen und den Tooltip zur BESCHREIBUNG —
 *   ohne es setzt MUI den Tooltip-Text als `aria-label` und überschreibt damit
 *   den sichtbaren Namen (WCAG 2.5.3).
 *
 * @useWhen
 *   - Ein englisches Steuer-/Fachwort braucht den deutschen Rückverweis
 *   - Das Etikett sitzt in einem nicht fokussierbaren Element (dt, th, h2, h3, label)
 *   - Ein Spaltenkopf trägt das Glossar für eine ganze Spalte (`begriff` = das Glossar)
 *
 * @dontUseFor
 *   - Rein dekorative Zusatzinfo ohne Begriff — dann `Tooltip` direkt
 *   - Einen Knopf mit eigener Handlung — der ist schon fokussierbar
 *   - Einen Wert in einer Tabellenzeile → `fokussierbar={false}`; das Glossar
 *     hängt am Kopf, sonst kostet jede Zeile einen eigenen Tab-Stopp
 *     (Regel-16-Warnung N1, Welle 3 PR 3)
 *
 * @related
 *   - Tooltip (MUI) — der Träger; `describeChild` verbindet ihn mit dem Auslöser
 *
 * @reuseHints
 *   - Weitere Begriffe der §3-Liste, sobald sie sichtbar werden
 *     (`Ausstellung`, `Nummernkreis`, `USt-IdNr.`, `Gutschrift`)
 *
 * @props
 *   label        string   der sichtbare (englische) Text
 *   begriff      string   der deutsche Begriff im Tooltip
 *   fokussierbar bool?    Vorgabe `true`. `false` nimmt den `tabIndex` weg —
 *                         für einen Wert in einer Tabellenzeile, dessen Glossar
 *                         am Kopf hängt; der Tooltip bleibt für Maus/Touch
 *   sx           object?  Layout am `<span>`
 */
export default function Fachbegriff({ label, begriff, sx, fokussierbar = true }) {
  return (
    <Tooltip title={begriff} describeChild>
      <Box
        component="span"
        tabIndex={fokussierbar ? 0 : undefined}
        sx={{ borderBottom: '1px dotted', borderColor: 'divider', cursor: 'help', ...sx }}
      >
        {label}
      </Box>
    </Tooltip>
  );
}
